import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import generateSolrQuery from "../../helpers/solr";
import { myCandidatesSelector } from "./redux/selector";
import { useDispatch, useSelector } from "react-redux";
import {
  getSearchSuggestions,
  getSolrJobsAction,
  setSearchInputs,
} from "./redux/action";
import { limit } from "../utils/constantValues";

const SearchHeader = () => {
  const {
    candidateDetails,
    candidateJobs,
    candidateJobsCount,
    suggestions,
    keywords,
    tabView,
  } = useSelector(myCandidatesSelector);
  const dispatch = useDispatch();

  // State for job type selection
  const [jobType, setJobType] = useState("All");
  const [locationShortRegion, setLocationShortRegion] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [location, setLocation] = useState("");
  const [title, setTitle] = useState("");
  // Matches API experience values like "0", "2+ years", "4-7 years", "5+ years"
  const [experience, setExperience] = useState("All");
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [titleSuggestions, setTitleSuggestions] = useState([])
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const experienceOptions = [
    { value: "All", label: "All" },
    { value: "0-3", label: "0-3 Years" },
    { value: "4-7", label: "4-7 Years" },
    { value: "8-10", label: "8-10 Years" },
    { value: "10+", label: "10+ Years" },
  ];

  const handleJobTypeChange = (event) => {
    setJobType(event.target.value);
  };

  const handleExperienceChange = (event) => {
    setExperience(event.target.value);
  };

  useEffect(() => {
    //console.log(suggestions);
    if (suggestions && suggestions.length > 0) {
      const names = suggestions?.map((item) => item.name);

      setCompanySuggestions(names);
      setLocationSuggestions(names);
      setTitleSuggestions(names)
    }
  }, [suggestions]);

  useEffect(() => {
    setCompanyName("");
    setLocation("");
    setTitle("");
    setExperience("All");

    setCompanySuggestions([]);
    setLocationSuggestions([]);
    setTitleSuggestions([]);
  }, [tabView]);

  const handleCompanyChange = (event, newValue) => {
    const data = {
      type: "company",
      input: newValue,
    };
    dispatch(getSearchSuggestions(data));
    setCompanyName(newValue);
    // Fetch company suggestions based on newValue
    if (newValue.length > 2) {
      setLoading(true);
      // Simulate fetching suggestions (replace with real API call)
    }
  };

  const handleLocationChange = (event, newValue) => {
    const data = {
      type: "location",
      input: newValue,
    };
    dispatch(getSearchSuggestions(data));
    setLocation(newValue);
    // Fetch location suggestions based on newValue
    if (newValue.length > 2) {
      setLoading(true);
      // Simulate fetching suggestions (replace with real API call)
    }
  };

  const handleTitleChange = (event, newValue) => {
    setTitle(newValue);
  };


  const onSearch = () => {
    const result = suggestions.find((item) => item.name === location);

    setLocationShortRegion(result?.shortRegion);

    let inputs = {
      searchCompanyName: companyName,
      searchLocation: result?.shortRegion || location,
      searchJobType: jobType,
      searchTitle: title,
      searchExperience: experience !== "All" ? experience : "",
    };

    dispatch(setSearchInputs(inputs));
    // if (candidateDetails && candidateDetails?.role) {
    //   let solrURL = getSolrQuery(0);
    //   dispatch(getSolrJobsAction(solrURL, 0));
    // }

    setCompanySuggestions([]);
    setLocationSuggestions([]);
    setTitleSuggestions([]);
  };

  const getSolrQuery = (updatedPage) => {
    setPage(updatedPage);
    const related_titles = candidateDetails?.related_titles;
    const role = candidateDetails?.role;

    if (role?.includes("/")) {
      const role_arr = role?.split("/");
      var combinedString = `("${role_arr?.join('" OR "')}")`;
    } else if (role?.includes("-")) {
      const role_arr = role?.split("-");
      var combinedString = `("${role_arr?.join('" OR "')}")`;
    } else if (role?.includes("(")) {
      combinedString = role;
    } else {
      combinedString = '"' + role + '"';
    }

    const str = related_titles;
    const jobtitle = combinedString;

    const removeKeyword = (word) => {
      const foundKeyword = keywords?.find((keyword) =>
        word.toLowerCase()?.includes(keyword.toLowerCase())
      );
      if (foundKeyword) {
        const regex = new RegExp(`\\b${foundKeyword}\\b|\\\\`, "gi");
        return word.replace(regex, "").trim();
      }
      return word;
    };

    const extractedWords = str?.includes(",")
      ? str
        .split(",")
        .map((item) => removeKeyword(item.trim()))
        .filter(Boolean)
      : removeKeyword(str.trim());
    const extractedJobtitle = removeKeyword(jobtitle.trim());

    const outputRelatedTitles = Array.isArray(extractedWords)
      ? `"${extractedWords.join('" OR "')}"`
      : `"${extractedWords}"`;

    let combinedStrWithRelatedTitles;

    if (title && title !== "") {
      combinedStrWithRelatedTitles = '"' + title + '"';
    } else {
      if (str !== "") {
        combinedStrWithRelatedTitles = `(${'"' + extractedJobtitle.substring(1, extractedJobtitle.length - 1) + '"'
          } OR ${outputRelatedTitles})`;
      } else {
        combinedStrWithRelatedTitles = extractedJobtitle;
      }
    }
    const sort_key = `posted_date desc`;
    const daysFilter = "[* TO *]";

    const generatedSolrQuery = generateSolrQuery(
      {
        jobtitle: combinedStrWithRelatedTitles,
        company_name: (companyName && '"' + companyName + '"') || "* TO *",
        location:
          (locationShortRegion && locationShortRegion) ||
          (location && location) ||
          "* TO *",
        jobtype_name: jobType && jobType !== "All" ? jobType : "* TO *",
        posted_date: daysFilter,
      },
      0,
      10,
      sort_key
    );
    return generatedSolrQuery;
  };

  return (
    <Box sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
      <Paper elevation={2} sx={{ padding: "16px" }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="center" // Centers the content horizontally
        >
          <Grid item xs={2.4}>
            <Autocomplete
              freeSolo
              value={companyName}
              onInputChange={handleCompanyChange}
              onBlur={() => {
                setCompanySuggestions([]);
                setLocationSuggestions([]);
                setTitleSuggestions([]);
              }}
              options={companySuggestions || []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search By Company Name"
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={2.4}>
            <Autocomplete
              freeSolo
              value={title}
              onInputChange={handleTitleChange}
              onBlur={() => {
                setCompanySuggestions([]);
                setLocationSuggestions([]);
                setTitleSuggestions([]);
              }}
              options={titleSuggestions || []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search By Title"
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={2.4}>
            <Autocomplete
              freeSolo
              value={location}
              onInputChange={handleLocationChange}
              onBlur={() => {
                setCompanySuggestions([]);
                setLocationSuggestions([]);
                setTitleSuggestions([])
              }}
              options={locationSuggestions || []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location"
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel id="experience-filter-label">Experience</InputLabel>
              <Select
                labelId="experience-filter-label"
                id="experience-filter"
                value={experience}
                label="Experience"
                onChange={handleExperienceChange}
              >
                {experienceOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* &nbsp; */}

          <Grid item xs={2}>
            <Button
              onClick={onSearch}
              variant="contained"
              color="primary"
              fullWidth
              size="small"
              sx={{ height: "45px" }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SearchHeader;
